import { useState } from 'react';

import { signIn } from 'aws-amplify/auth';

import useBackendApi from './useBackendApi';
import { RequestOptions } from '../types/httpRequests';
import { addIntCodeToPhoneNumber, getInitialUserThemeValue, sendConfirmationCode } from '../utils/user';
import { fetchUserMembershipSummary, getAccountLandingPath } from '../utils/userMembership';

interface UserDataType {
  sub: string;
  email: string;
  gender: string;
  birthdate: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  address_1: string;
  address_2: string;
  country: string;
  city: string;
  state: string;
  zipcode: string;
  tos_flag: boolean;
  tos_date: string;
  marketing_flag: boolean;
  marketing_date: string;
  occupation: string;
  theme: string;
  sms_flag: boolean;
  sms_date: string;
  hipaa_npp_flag: boolean;
  hipaa_npp_date: string;
  hipaa_copy_req_flag: boolean;
  hipaa_copy_req_date: string;
}

interface SignUpDataI {
  username: string;
  options: {
    userAttributes: {
      email: string;
      name: string;
      family_name: string;
      [key: string]: string;
    };
  };
}

const backendURL = process.env.REACT_APP_BACKEND_URL;
const app_co = process.env.REACT_APP_COMPANY;

export const useRegistration = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [userData, setUserData] = useState<UserDataType>({} as UserDataType);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteDetails, setInviteDetails] = useState<any>(null);
  const [inviteAcceptanceStatus, setInviteAcceptanceStatus] = useState<'idle' | 'success' | 'error' | 'skipped'>(
    'idle',
  );
  const [inviteAcceptanceError, setInviteAcceptanceError] = useState<string | null>(null);
  const { apiFetchRequest } = useBackendApi();

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const getInviteRedirectPath = () => {
    const orgPathName = inviteDetails?.organization?.path_name;
    if (!orgPathName) return '/';

    const organizationPath = `/o/${encodeURIComponent(orgPathName)}`;
    if (inviteDetails?.invite?.invite_scope?.toLowerCase() !== 'acc') {
      return organizationPath;
    }

    const accountPathName = inviteDetails?.account_path_name;
    return accountPathName ? `${organizationPath}/a/${encodeURIComponent(accountPathName)}` : '/';
  };

  const handleComplete = () => {
    window.location.replace(getInviteRedirectPath());
  };

  const redirectToFirstAccountMembership = async (): Promise<boolean> => {
    if (app_co !== 'frost') {
      return false;
    }

    try {
      const membershipSummary = await fetchUserMembershipSummary(apiFetchRequest);
      const accountLandingPath = getAccountLandingPath(membershipSummary?.memberships ?? []);

      if (!accountLandingPath) {
        return false;
      }

      window.location.replace(accountLandingPath);
      return true;
    } catch (error) {
      console.error('Failed to resolve post-registration account:', error);
      return false;
    }
  };

  const handleDatabaseUserCreation = async (userDetails: any) => {
    const endpointUrl: string = `${backendURL}/users/`;
    const apiRequestOptions: RequestOptions = {
      body: userDetails,
      method: 'POST',
      endpoint: endpointUrl,
    };

    return await apiFetchRequest(apiRequestOptions);
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    setError('');
    try {
      switch (activeStep) {
        case 0:
          setUserData(values);
          handleNext();
          break;
        case 1:
          try {
            const currentTime = new Date().toISOString();
            const updatedAttributes: {
              [key: string]: string | number;
            } = {
              'custom:address_1': values.address_1,
              'custom:address_2': values.address_2,
              'custom:country': 'US',
              'custom:city': values.city,
              'custom:state': values.state,
              'custom:zipcode': values.zipcode,
              'custom:tos_flag': userData.tos_flag ? '1' : '0',
              'custom:tos_date': currentTime,
              'custom:marketing_flag': userData.marketing_flag ? '1' : '0',
              'custom:marketing_date': userData.marketing_flag ? currentTime : '',
              'custom:occupation': values.occupation,
              'custom:theme': getInitialUserThemeValue(),
              'custom:sms_flag': userData.sms_flag ? '1' : '0',
              'custom:sms_date': userData.sms_flag ? currentTime : '',
              'custom:hipaa_npp_flag': userData.hipaa_npp_flag ? '1' : '0',
              'custom:hipaa_npp_date': userData.hipaa_npp_flag ? currentTime : '',
              'custom:hipaa_copy_req_flag': userData.hipaa_copy_req_flag ? '1' : '0',
              'custom:hipaa_copy_req_date': userData.hipaa_copy_req_flag ? currentTime : '',
            };

            const phoneNumber = addIntCodeToPhoneNumber(values.phoneNumber);

            const signUpData: SignUpDataI = {
              username: userData.email,
              options: {
                userAttributes: {
                  email: userData.email,
                  name: userData.firstName,
                  gender: values.gender,
                  family_name: userData.lastName,
                  phone_number: phoneNumber,
                  birthdate: values.birthdate,
                  ...updatedAttributes,
                },
              },
            };

            setUserData({
              ...userData,
              ...values,
              phoneNumber: phoneNumber,
              sub: '',
              country: 'US',
              tos_flag: userData.tos_flag ? '1' : '0',
              tos_date: currentTime,
              marketing_flag: userData.marketing_flag ? '1' : '0',
              marketing_date: userData.marketing_flag ? currentTime : '',
              theme: getInitialUserThemeValue(),
              sms_flag: userData.sms_flag ? '1' : '0',
              sms_date: userData.sms_flag ? currentTime : '',
              hipaa_npp_flag: userData.hipaa_npp_flag ? '1' : '0',
              hipaa_npp_date: userData.hipaa_npp_flag ? currentTime : '',
              hipaa_copy_req_flag: userData.hipaa_copy_req_flag ? '1' : '0',
              hipaa_copy_req_date: userData.hipaa_copy_req_flag ? currentTime : '',
            });

            const scc_r = await sendConfirmationCode(
              { email: userData.email, phone: phoneNumber, sms_flag: userData.sms_flag ? '1' : '0' },
              apiFetchRequest,
            );
            handleNext();
          } catch (signUpError: any) {
            setError('An error ocurred during registration. Please try again.');
          }
          break;
        case 2:
          try {
            let userDetails = {
              confirmation_code: values.confirmationCode,
              sub: userData.sub,
              username: userData.email,
              email: userData.email,
              first_name: userData.firstName,
              last_name: userData.lastName,
              gender: userData.gender,
              birthdate: userData.birthdate,
              phone_number: userData.phoneNumber,
              address_1: userData.address_1,
              address_2: userData.address_2,
              city: userData.city,
              state: userData.state,
              zipcode: userData.zipcode,
              country: userData.country,
              occupation: userData.occupation,
              tos_flag: userData.tos_flag ? '1' : '0',
              tos_date: userData.tos_date,
              marketing_flag: userData.marketing_flag,
              marketing_date: userData.marketing_date,
              theme: userData.theme,
              sms_flag: userData.sms_flag,
              sms_date: userData.sms_date,
              hipaa_npp_flag: userData.hipaa_npp_flag,
              hipaa_npp_date: userData.hipaa_npp_date,
              hipaa_copy_req_flag: userData.hipaa_copy_req_flag,
              hipaa_copy_req_date: userData.hipaa_copy_req_date,
            };

            const userCreationResponse = await handleDatabaseUserCreation(userDetails);

            await signIn({
              username: userData.email,
              password: userCreationResponse.body.password,
            });

            const inviteAcceptanceResponse = await acceptInviteIfPresent();
            if (inviteAcceptanceResponse && !inviteAcceptanceResponse.isError) {
              setIsSubmitting(false);
              window.location.replace(getInviteRedirectPath());
              return;
            }

            if (!inviteToken && (await redirectToFirstAccountMembership())) {
              setIsSubmitting(false);
              return;
            }

            handleNext();
          } catch (confirmationError: any) {
            setError('Invalid confirmation code. Please try again.');
          }
          break;
        default:
          console.log('Registration complete');
      }
    } catch (error) {
      console.error('Error during the user registration:', error);
    }
    setIsSubmitting(false);
  };

  const handleResendCode = async (setResendSubmitting: any, setMessage: any) => {
    setResendSubmitting(true);
    try {
      const scc_r = await sendConfirmationCode(
        { email: userData.email, phone: userData.phoneNumber, sms_flag: userData.sms_flag ? '1' : '0' },
        apiFetchRequest,
      );
    } catch (signUpError: any) {
      setMessage({
        text: 'Error resending confirmation code:',
        severity: 'error',
      });
    } finally {
      // just to avoid spamming the button
      setTimeout(() => {
        setResendSubmitting(false);
      }, 4000);
    }
  };

  const fetchAppointmentDetails = async (token: string, setApptDetailsNotFound: any, setLoading: any) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/appointment/${token}`,
        method: 'GET',
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch appointment details:', error);
      setApptDetailsNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientBookingDetails = async (token: string, setClientBookingDetailsNotFound: any, setLoading: any) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/client-booking/${token}`,
        method: 'GET',
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch client booking details:', error);
      setClientBookingDetailsNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormLinks = async (token: string, setFormLinksNotFound: any, setLoading: any) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/forms/${token}`,
        method: 'GET',
      });

      return response;
    } catch (error) {
      console.error('Failed to fetch form links details:', error);
      setFormLinksNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchInviteDetails = async (token: string, setInviteNotFound: any, setLoading: any) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/invites/${token}`,
        method: 'GET',
      });

      if (!response?.isError) {
        setInviteToken(token);
        setInviteDetails(response.body);
        setInviteAcceptanceStatus('idle');
        setInviteAcceptanceError(null);
      } else {
        setInviteToken(null);
        setInviteDetails(null);
        setInviteAcceptanceStatus('idle');
        setInviteAcceptanceError(null);
        setInviteNotFound(true);
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch invite details:', error);
      setInviteToken(null);
      setInviteDetails(null);
      setInviteAcceptanceStatus('idle');
      setInviteAcceptanceError(null);
      setInviteNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const acceptInviteIfPresent = async () => {
    if (!inviteToken) {
      setInviteAcceptanceStatus('skipped');
      return null;
    }

    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/invites/${inviteToken}/accept`,
        method: 'POST',
      });

      if (response?.isError) {
        const detail = response?.body?.detail || 'Unable to accept invite.';
        setInviteAcceptanceStatus('error');
        setInviteAcceptanceError(detail);
        return response;
      }

      setInviteAcceptanceStatus('success');
      setInviteAcceptanceError(null);
      setInviteToken(null);
      return response;
    } catch (error) {
      console.error('Failed to accept invite:', error);
      setInviteAcceptanceStatus('error');
      setInviteAcceptanceError('Unable to accept invite. Please try again later.');
      return null;
    }
  };

  return {
    isSubmitting,
    activeStep,
    error,
    userData,
    handleSubmit,
    handleComplete,
    handleResendCode,
    fetchAppointmentDetails,
    fetchClientBookingDetails,
    fetchFormLinks,
    fetchInviteDetails,
    inviteDetails,
    inviteAcceptanceStatus,
    inviteAcceptanceError,
  };
};
